import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    AxButton,
    AxCard,
    AxIconWrapper,
    AxInput,
    AxPassword,
    AxPopover,
} from "@dynata/axiom3";
import useForgotPasswordVerification from "./hooks/useForgotPasswordVerification.hook";
import paths from "paths"
import Error from "../../../core/widgets/error";
import "../../../scss/forgot-password.scss";
import isPasswordCorrect from "../../../services/validation";
import { toast, useToast } from "react-toastify";
import { ToastConfig } from "core/widgets/toastConfig.widget";

const MISMATCHING_PASSWORDS_ERROR = "Passwords do not match";
const EMPTY_PASSWORD_ERROR = "Password required";
const EMPTY_EMAIL_ERROR = "Email required";

export const ForgotPasswordValidationPage: React.FC = () => {
    const navigate = useNavigate();
    const { code } = useParams();

    const [passwordCheckError, setPasswordCheckError] = useState<string>();
    const [passwordError, setPasswordError] = useState<string>();
    const [emailError, setEmailError] = useState<string>();
    const [passwordCheck, setPasswordCheck] = useState("");
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState("");
    const [email, setEmail] = useState("");

    const forgotPassword = useForgotPasswordVerification();
    async function changePassword() {
        setPasswordCheckError(undefined);
        setPasswordError(undefined);
        setEmailError(undefined);

        if (!email.trim()) {
            setEmailError(EMPTY_EMAIL_ERROR);
            return;
        }

        if (!password.trim()) {
            setPasswordError(EMPTY_PASSWORD_ERROR);
            return;
        }

        if (!passwordCheck.trim()) {
            setPasswordCheckError(EMPTY_PASSWORD_ERROR);
            return;
        }

        if (password !== passwordCheck) {
            setPasswordError(MISMATCHING_PASSWORDS_ERROR);
            return;
        }

        let result = isPasswordCorrect(password);
        if (!result.success) {
            setPasswordError(result.error.message);
            return;
        }
        setLoading(true);
        forgotPassword.mutateAsync({ email, password, code }).then((_) => {
            toast.success("Successfully changed password!", ToastConfig)
            navigate(`/${paths.open.LOGIN}`)
        }).catch((_) => {
            toast.error("The reset password link has expired or isn't valid. Please try requesting a new link.", ToastConfig)
            navigate(`/${paths.open.RESET}`)
        }).finally(() => setLoading(false));
    }

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            changePassword();
        }
    };

    return (
        <div>
            <div className="forgot-password-verification-body">
                <AxCard
                    style={{ width: 400, margin: "auto" }}
                    radius="medium"
                    shadow="medium"
                    actions={[
                        <AxButton
                            onClick={changePassword}
                            disabled={
                                !password.trim() || !email.trim() || !passwordCheck.trim()
                                    ? "disabled"
                                    : ""
                            }
                            className="button-right"
                            loading={loading}
                            type="primary"
                            color="main"
                        >
                            Submit
                        </AxButton>,
                    ]}
                >
                    <AxInput
                        prefix={<i className="fa-regular fa-envelope" />}
                        onChange={(e: any) => {
                            setEmailError(undefined);
                            if (!e.target.value.trim()) {
                                setEmailError(EMPTY_EMAIL_ERROR);
                            }
                            setEmail(e.target.value);
                        }}
                        placeholder="Email"
                        value={email}
                        size="large"
                        onKeyPress={handleKeyPress}
                    />
                    <Error error={emailError} />
                    <AxPassword
                        onChange={(e: any) => {
                            setPasswordError(undefined);
                            if (!e.target.value.trim()) {
                                setPasswordError(EMPTY_PASSWORD_ERROR);
                            }
                            setPassword(e.target.value);
                        }}
                        prefix={<i className="fa-regular fa-key" />}
                        placeholder="New password"
                        value={password}
                        size="large"
                        onKeyPress={handleKeyPress}
                    />
                    <Error error={passwordError} />
                    <AxPassword
                        onChange={(e: any) => {
                            setPasswordCheckError(undefined);
                            if (!e.target.value.trim()) {
                                setPasswordCheckError(EMPTY_PASSWORD_ERROR);
                            }
                            setPasswordCheck(e.target.value);
                        }}
                        prefix={<i className="fa-regular fa-key" />}
                        placeholder="Reenter new password"
                        value={passwordCheck}
                        size="large"
                        onKeyPress={handleKeyPress}
                    />
                    <Error error={passwordCheckError} />
                    <AxPopover
                        style={{
                            width: 500,
                        }}
                        content={
                            <div>
                                <p>One uppercase letter</p>
                                <p>One lowercase letter</p>
                                <p>One number</p>
                                <p>
                                    One special character
                                    <br />
                                    {'(!~<>,;:_=?*+#."&%()|$^@/)'}
                                </p>
                            </div>
                        }
                        title="Password Requirements"
                    >
                        <AxIconWrapper type="regular" icon={"fa-circle-info"} />
                    </AxPopover>
                </AxCard>
            </div>
        </div>
    );
};
